import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormikContext } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import MuiLink from '@material-ui/core/Link';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Field } from 'formik';
import { TextField, PhoneNumberInput } from 'components/form/Formik';
import FormikReactSelect from 'components/form/Formik/FormikReactSelect';
import { useSnackbar } from 'notistack';
import useAlert from 'hooks/useAlert';
import { recoverAccountId } from 'utils/api';
import { FORM_VALIDATION, ACCOUNT_NUMBER_FIELD_STYLES } from 'constants/addressForm';

const useStyles = makeStyles((theme) => ({
  accountNumberField: ACCOUNT_NUMBER_FIELD_STYLES,
}));

const CustomerInfoForm = ({ isPickup, countries, shippingStates }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const alert = useAlert();
  const { globalOptions } = useSelector((s) => s.lab);

  useEffect(() => {
    // delete old customerDetails from localStorage if it exists
    // can remove this after a few months
    localStorage.removeItem('customerDetails');
  }, []);

  const onSendAccountNumber = (e) => {
    e.preventDefault();
    alert.prompt({
      title: 'Check for Lab Account Number',
      description:
        'Enter your email address and we will send you an email with your account number.',
      onConfirm: async (value) => {
        try {
          await recoverAccountId(value);
          enqueueSnackbar('Email Sent!', {
            variant: 'success',
          });
        } catch (e) {
          enqueueSnackbar(e.message, { variant: 'error' });
        }
      },
    });
  };

  return (
    <Box>
      {globalOptions.require_userid && (
        <>
          <TextField
            fullWidth
            type="number"
            name="customer.userId"
            label="Account Number (*optional, leave blank if you don't know it)"
            className={classes.accountNumberField}
            onWheel={(event) => event.target.blur()}
          />
          <Typography variant="caption">
            *If you are a reseller the Account Number is Required.{' '}
            <MuiLink href="#" onClick={onSendAccountNumber}>
              Need your Account Number?
            </MuiLink>
          </Typography>
        </>
      )}
      <TextField
        fullWidth
        label="First Name"
        name="customer.firstName"
        validate={FORM_VALIDATION.required}
      />
      <TextField
        fullWidth
        label="Last Name"
        name="customer.lastName"
        validate={FORM_VALIDATION.required}
      />
      <TextField
        fullWidth
        label="Business Name (optional)"
        name="customer.businessName"
      />
      <PhoneNumberInput
        fullWidth
        type="tel"
        label="Phone"
        name="customer.phone"
        validate={FORM_VALIDATION.required}
      />
      <TextField
        fullWidth
        type="email"
        label="Email"
        name="customer.email"
        validate={FORM_VALIDATION.required}
      />
      {!isPickup && (
        <>
          <TextField
            fullWidth
            label="Address"
            name="customer.address1"
            validate={FORM_VALIDATION.required}
          />
          <TextField
            fullWidth
            label="Address line 2"
            name="customer.address2"
          />
          <TextField
            fullWidth
            label="City"
            name="customer.city"
            validate={FORM_VALIDATION.required}
          />
          {countries.length > 1 ? (
            <Field
              fullWidth
              transformValue
              label="Country"
              name="customer.country"
              component={FormikReactSelect}
              options={countries.map((i) => ({
                label: i.country.name,
                value: i.country.code,
              }))}
              validate={FORM_VALIDATION.required}
            />
          ) : null}
          <label
            style={{
              color: 'rgba(0, 0, 0, 0.54)',
              display: 'block',
              position: 'initial',
              transform: 'scale(1)',
              marginBottom: '8px',
              fontSize: '1rem',
            }}
          >
            State
          </label>
          <Field name="customer.state" validate={FORM_VALIDATION.required}>
            {({ field, form: { touched, errors } }) => (
              <FormControl
                fullWidth
                error={touched.customer?.state && !!errors.customer?.state}
              >
                <select
                  {...field}
                  autoComplete="address-level1"
                  style={{
                    borderRadius: 0,
                    border: '1px solid #e0e0e0',
                    padding: '12px 16px',
                    fontSize: '14px',
                    width: '100%',
                    backgroundColor: 'transparent',
                    color: '#000',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#1976d2';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                >
                  <option value="">Select State</option>
                  {shippingStates.map((state) => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
                {touched.customer?.state && errors.customer?.state && (
                  <FormHelperText error>{errors.customer.state}</FormHelperText>
                )}
              </FormControl>
            )}
          </Field>
          <TextField
            fullWidth
            label="Zip"
            name="customer.zip"
            validate={FORM_VALIDATION.required}
          />
        </>
      )}
    </Box>
  );
};

export default CustomerInfoForm; 